import type { NotificationRequest, NotificationResponse } from '@/api/data-contracts';
import { parseNotificationExtraDetails } from '@/features/notifications';
import { tryParseJson } from '@/shared/lib/json';

export type DashboardAlertKind = 'today' | 'print';

type AlertEventType = NonNullable<NotificationRequest['eventTypes']>[number];

const SEVERITY_RANK: Record<NotificationResponse['severity'], number> = {
  CRITICAL: 0,
  IMPORTANT: 1,
  COMMON: 2,
};

export const ALERT_EVENT_TYPES: Record<DashboardAlertKind, AlertEventType[]> = {
  today: [
    'SERVICE_TASK_OUTDOOR_WEATHER_ALERT',
    'ORDER_UPDATE_LOCATION',
    'ORDER_DELIVERY_STATUS_CHECK',
    'ORDER_DECORATION_TASK_IN_DELIVERY',
    'ORDER_NEUTRALIZATION_STATUS_CHECK',
    'ORDER_DECORATION_STATUS_CHECK',
    'ORDER_DECORATION_DAYTIME_IMAGES',
    'SERVICE_TASK_DATES_CHANGED',
  ],
  print: [
    'ORDER_READY_FOR_PRINTING',
    'ORDER_DTP_REVIEW_STARTED',
    'ORDER_DTP_APPROVED',
    'ORDER_PRINT_PROOF_ARTWORK_RECEIVED',
    'ARTWORK_PREVIEW_UPLOAD_NEEDED',
    'ARTWORK_PREVIEW_UPLOADED',
    'ARTWORK_PREVIEW_APPROVED',
    'ARTWORK_PREVIEW_REJECTED',
    'ARTWORK_PREVIEW_COMMENT_ADDED',
    'ORDER_BOOKING_ARTWORK_MATCH_UPDATED',
    'ORDER_BOOKING_ARTWORK_MATCH_DELETED',
  ],
};

export const sortAlerts = (alerts: NotificationResponse[]): NotificationResponse[] =>
  [...alerts].sort(
    (a, b) =>
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      new Date(b.audit.timestamp).getTime() - new Date(a.audit.timestamp).getTime(),
  );

const ORDER_FIELDS = ['orderNumber', 'orderId', 'rootObjectId'];
const SUBJECT_FIELDS = [...ORDER_FIELDS, 'serviceTaskId', 'taskId'];

const alertDetails = (alert: NotificationResponse): Record<string, unknown> => ({
  ...parseNotificationExtraDetails(alert.audit.extraDetails),
  ...tryParseJson(alert.audit.details ?? '{}'),
});

const firstValue = (details: Record<string, unknown>, fields: string[]): string | undefined =>
  fields
    .map((field) => details[field])
    .find((candidate): candidate is string => typeof candidate === 'string' && candidate !== '');

const subjectOf = (alert: NotificationResponse): string | undefined =>
  firstValue(alertDetails(alert), SUBJECT_FIELDS);

export const orderRefOf = (alert: NotificationResponse): string | undefined =>
  firstValue(alertDetails(alert), ORDER_FIELDS);

export const withoutInactiveOrders = (
  alerts: NotificationResponse[],
  activeOrderRefs: ReadonlySet<string>,
): NotificationResponse[] =>
  alerts.filter((alert) => {
    const orderRef = orderRefOf(alert);
    return !orderRef || activeOrderRefs.has(orderRef);
  });

export const latestPerOrderAndType = (alerts: NotificationResponse[]): NotificationResponse[] => {
  const newestFirst = [...alerts].sort(
    (a, b) => new Date(b.audit.timestamp).getTime() - new Date(a.audit.timestamp).getTime(),
  );
  const seen = new Set<string>();
  return newestFirst.filter((alert) => {
    const subject = subjectOf(alert);
    if (!subject) return true;
    const key = `${alert.audit.type}:${subject}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
